<script setup lang="ts">
/**
 * Health Check: any problem opened in place — what's wrong, on what, since when, the facts behind it, and the
 * page that fixes it — so the list stays on screen. Content problems have their own richer panels.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { HealthIssue } from '@codot/contracts';
import { CODE_LABEL, fixLink, issueMessage, KIND_LABEL, SEVERITY, since } from '../utils/health';
import { formatInstant } from '../utils/time';
import SlideOverDrawer from './SlideOverDrawer.vue';

const props = defineProps<{ issue: HealthIssue | null }>();
const emit = defineEmits<{ close: [] }>();
const { t, te } = useI18n();

/** The facts the rule recorded, minus internal ids. */
const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const HIDDEN = new Set(['code', 'playerIds', 'assetIds', 'screenId']);
const facts = computed(() =>
  Object.entries(props.issue?.details ?? {})
    .filter(([k, v]) => !HIDDEN.has(k) && v !== null && v !== '')
    .map(([k, v]) => ({
      key: k,
      label: te(`health.fact_${k}`) ? t(`health.fact_${k}`) : k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()),
      value:
        typeof v === 'boolean'
          ? v ? t('health.yes') : t('health.no')
          : k === 'syncError'
            ? te(`sync.err_${String(v)}`) ? t(`sync.err_${String(v)}`) : String(v)
          : typeof v === 'string' && ISO.test(v)
            ? `${formatInstant(v, { dateStyle: 'medium', timeStyle: 'short' })} (${since(v)})`
            : String(v).replace(/_/g, ' '),
    })),
);
const fix = computed(() => (props.issue ? fixLink(props.issue) : null));
</script>

<template>
  <SlideOverDrawer :open="!!issue" :title="issue ? CODE_LABEL[issue.code] : ''" :description="issue ? `${KIND_LABEL[issue.kind]} · ${issue.subjectName}` : undefined" size="md" @close="emit('close')">
    <div v-if="issue" class="space-y-5 text-sm">
      <section class="rounded-lg border p-3" :class="issue.severity === 'info' ? 'border-sky-200 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/40' : issue.severity === 'warning' ? 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40' : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40'">
        <span class="rounded px-1.5 py-0.5 text-[11px] font-medium" :class="SEVERITY[issue.severity].chip">{{ SEVERITY[issue.severity].label }}</span>
        <p class="mt-2 font-medium">{{ issueMessage(issue) }}</p>
        <p class="mt-1 text-xs text-slate-600 dark:text-slate-400">{{ t('notify.openFor', { since: since(issue.openedAt) }) }}</p>
      </section>
      <section v-if="facts.length" aria-labelledby="facts-h">
        <h3 id="facts-h" class="font-semibold">{{ t('notify.facts') }}</h3>
        <dl class="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          <div v-for="f in facts" :key="f.key" class="flex justify-between gap-4 px-3 py-2">
            <dt class="text-slate-500">{{ f.label }}</dt>
            <dd class="min-w-0 text-end font-medium break-words">{{ f.value }}</dd>
          </div>
        </dl>
      </section>
      <p class="text-xs leading-relaxed text-slate-500">{{ t('notify.autoResolve') }}</p>
    </div>
    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <button type="button" class="btn-secondary" @click="emit('close')">{{ t('content.close') }}</button>
        <RouterLink v-if="fix" :to="fix.to" class="btn-primary" @click="emit('close')">{{ fix.label }} →</RouterLink>
      </div>
    </template>
  </SlideOverDrawer>
</template>
